import { Controller } from "@hotwired/stimulus"
import { buildLocalizedUrl } from "../lib/locale"

// 댓글 모니터링 컨트롤러
// 댓글 모니터링 페이지의 인터랙션을 처리합니다.
export default class extends Controller {
  static targets = [
    "responseTemplate",
    "socialAccount",
    "postId",
    "postUrl",
    "chatbotNotification",
    "postsContainer",
    "postsEmpty",
    "postsGrid",
    "templateSelectorContainer",
    "postsControls",
    "searchInput",
    "sortSelect",
    "matchAll",
    "keywordsSection",
    // 추가 타겟: 적용 범위 배지 및 키워드 범위 안내
    "scopeBadge",
    "keywordsScopeNote",
    // 템플릿 선택기 UX 개선
    "templateSearch",
    "templateCard",
    "publicSelectionCount",
    "submitButton",
    "textPreview",
    "textContentCounter",
    "quickRoot",
    "notificationChannels"
  ]
  static values = {
    nameRequiredMessage: String,
    allModeRequiresPostMessage: String,
    keywordRequiredMessage: String,
    actionRequiredMessage: String,
    publicReplyMinCountMessage: String,
    saveErrorMessage: String,
    forceSaveErrorMessage: String,
    quickCreateNameRequiredMessage: String,
    quickCreateContentRequiredMessage: String,
    quickCreateMinButtonsRequiredMessage: String,
    quickCreateMissingButtonValueAtMessage: String,
    quickCreateMissingButtonPromptAtMessage: String,
    quickCreateCreateFailedMessage: String,
    quickCreateUnexpectedErrorMessage: String,
    scopeSelectedPostLabel: String,
    scopeAccountWideLabel: String,

    // i18n values for quick preview and placeholders
    previewTextPlaceholder: String,
    timeJustNow: String,
    templateNamePlaceholder: String,
    buttonBodyPlaceholder: String,
    buttonPreviewEmptyPlaceholder: String,
    cardTitlePlaceholder: String,
    cardSubtitlePlaceholder: String,
    cardPreviewEmptyPlaceholder: String,
    cardPreviewDefaultLinkWithUrl: String,
    cardPreviewNoDefaultLink: String,
    cardImageAlt: String,
    cardImageNotSet: String,
    avatarMeLabel: String,
    valueRequiredMessage: String,
    buttonContentCounterFormat: String,
    textContentCounterFormat: String,

    // Button type labels (used in previews)
    buttonTypeLabelUrl: String,
    buttonTypeLabelWebUrl: String,
    buttonTypeLabelPhone: String,
    buttonTypeLabelPostback: String,
    buttonTypeLabelTemplateCall: String,

    // Button row placeholders and hints
    buttonValuePlaceholderUrl: String,
    buttonValuePlaceholderPhone: String,
    buttonValuePlaceholderPostback: String,
    buttonHintUrl: String,
    buttonHintPhone: String,
    buttonHintPostback: String,
    buttonHintTemplateCall: String,

    // Card button row placeholders and hints
    cardButtonValuePlaceholderWebUrl: String,
    cardButtonValuePlaceholderPhone: String,
    cardButtonValuePlaceholderPostback: String,
    cardButtonHintWebUrl: String,
    cardButtonHintPhone: String,
    cardButtonHintPostback: String,
    cardButtonHintTemplateCall: String
  }

  connect() {
    console.log("🔧 CommentMonitoringController 연결됨!")
    console.log("📋 사용 가능한 타겟들:", this.targets)

    // 현재 선택된 계정 저장
    this.currentAccountId = null

    // 초기 상태 설정
    // 템플릿 선택기는 이미 서버 렌더링으로 채워져 있으면 추가 요청을 생략
    if (this.hasTemplateSelectorContainerTarget && this.templateSelectorContainerTarget && this.templateSelectorContainerTarget.children.length === 0) {
      this.handleActionTypeChange()
    }

    // 초기 "모든 댓글" 모드 상태 설정 (포스트 미선택이므로 비활성화)
    this.initializeModeState()

    // 상위 모드에 따른 UI 초기 동기화
    this.syncModeUI()

    // 공개 답글 선택 개수 초기 카운트
    this.updatePublicSelectionCount()

    // 알림 채널 토글 상태 초기화
    this.toggleNotificationChannelOptions(this.getCurrentActionType())

    // 빠른 생성 UI 초기화
    this.initializeQuickCreateForms()
    this.updateQuickPreview()

    // 편집 시 선택된 포스트 강조
    this.highlightSelectedPost()
  }

  // 미리보기용 간단한 HTML 이스케이프
  escapeHtml(str) {
    if (str == null) return ''
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  applySampleVariables(str) {
    if (!str) return ''
    const samples = {
      username: 'user123',
      comment_text: '가격이 어떻게 되나요?',
      post_url: 'https://instagram.com/p/abc123',
      product_name: '스프링 쇼퍼백'
    }
    let result = String(str)
    Object.entries(samples).forEach(([key, value]) => {
      const pattern = new RegExp(`\\{${key}\\}`, 'g')
      result = result.replace(pattern, value)
    })
    return result
  }

  // 폼 제출 유효성 검사
  submit(event) {
    try {
      // 규칙명
      const nameInput = this.element.querySelector('input[name="comment_monitoring_rule[name]"]')
      if (!nameInput || !nameInput.value.trim()) {
        event.preventDefault()
        alert(this.nameRequiredMessageValue || 'Please enter a rule name.')
        nameInput?.focus()
        return false
      }

      // 계정 선택 검증은 사용하지 않음 (잔재 기능 제거)

      // 현재 선택된 모드
      const mode = this.getSelectedMode()
      console.log('현재 모드:', mode)

      // 포스트 선택 여부 확인
      const postIdInput = this.element.querySelector('input[name="comment_monitoring_rule[post_id]"]')
      const hasPostSelected = !!(postIdInput && postIdInput.value && postIdInput.value.trim())

      // 모드별 유효성 검사
      if (mode === 'all') {
        // "모든 댓글" 모드: 포스트 선택 필수
        if (!hasPostSelected) {
          event.preventDefault()
          alert(this.allModeRequiresPostMessageValue || 'Select a post to use "All comments" mode.')
          return false
        }
      } else {
        // "키워드 기준" 모드: 키워드 입력 필수
        const keywordsInput = this.element.querySelector('input[name="comment_monitoring_rule[keywords]"]')
        if (!keywordsInput || !keywordsInput.value.trim()) {
          event.preventDefault()
          alert(this.keywordRequiredMessageValue || 'Please enter at least one keyword.')
          keywordsInput?.focus()
          return false
        }
      }

      // 응답 방식 선택
      const actionSelected = this.element.querySelector('input[name="comment_monitoring_rule[action_type]"]:checked')
      if (!actionSelected) {
        event.preventDefault()
        alert(this.actionRequiredMessageValue || 'Select one of reply/DM/notification.')
        return false
      }

      // 공개 답글 선택 최소 개수 검증
      if (actionSelected.value === 'public_reply' || actionSelected.value === 'reply_and_dm') {
        const prChecked = this.element.querySelectorAll('input[name="comment_monitoring_rule[settings][public_reply_template_ids][]"]:checked')
        const cnt = prChecked ? prChecked.length : 0
        if (cnt < 3) {
          event.preventDefault()
          alert(this.publicReplyMinCountMessageValue || 'Select at least 3 public reply templates.')
          return false
        }
      }

      // 통과 → 제출 진행
      return true
    } catch (e) {
      console.error('모니터링 규칙 제출 중 오류:', e)
      // 안전을 위해 기본 제출 막고 사용자에게 알림
      event.preventDefault()
      alert(this.saveErrorMessageValue || 'An error occurred while saving. Please check inputs.')
      return false
    }
  }

  // 강제 제출(클릭 버튼에서 호출)
  forceSubmit(event) {
    // 기존 submit 유효성 로직을 재사용하기 위해 가짜 이벤트를 생성
    const fakeEvent = { preventDefault: () => {}, target: this.element }
    const ok = this.submit(fakeEvent)
    if (ok !== false) {
      try {
        if (typeof this.element.requestSubmit === 'function') {
          this.element.requestSubmit()
        } else {
          this.element.submit()
        }
      } catch (e) {
        console.error('강제 제출 중 오류:', e)
        alert(this.forceSaveErrorMessageValue || 'An error occurred while saving. Please try again.')
      }
    }
  }

  // 현재 선택된 모드 반환
  getSelectedMode() {
    const el = this.element.querySelector('input[name="comment_monitoring_rule[settings][mode]"]:checked')
    return el ? el.value : 'keyword'
  }

  // 모드 변경 핸들러
  handleModeChange(event) {
    this.syncModeUI()
  }

  // 모드에 따른 UI 동기화 및 hidden 값 반영
  syncModeUI() {
    const mode = this.getSelectedMode()

    // 키워드 섹션 표시/숨김
    if (this.hasKeywordsSectionTarget) {
      const wrap = this.keywordsSectionTarget
      if (mode === 'all') {
        wrap.classList.add('hidden')
      } else {
        wrap.classList.remove('hidden')
      }
    }

    // hidden match_all 값 동기화
    if (this.hasMatchAllTarget) {
      this.matchAllTarget.value = (mode === 'all') ? '1' : '0'
    }

    // 키워드 범위 안내(계정 전체 문구) 토글
    try {
      if (this.hasKeywordsScopeNoteTarget) {
        const postIdInput = this.element.querySelector('input[name="comment_monitoring_rule[post_id]"]')
        const hasPostSelected = !!(postIdInput && postIdInput.value && postIdInput.value.trim())
        if (!hasPostSelected && mode === 'keyword') {
          this.keywordsScopeNoteTarget.classList.remove('hidden')
        } else {
          this.keywordsScopeNoteTarget.classList.add('hidden')
        }
      }
    } catch (e) { console.warn('keywordsScopeNote toggle error:', e) }
  }

  // 계정 변경/포스트 로딩 관련 기능은 제거
  
  // 검색/정렬 컨트롤 관련 기능은 제거 (계정 선택 기능 잔재)
  
  // 템플릿 검색 필터링
  filterTemplates() {
    if (!this.hasTemplateSearchTarget) return
    const q = this.templateSearchTarget.value.toLowerCase().trim()
    this.templateCardTargets.forEach((el) => {
      const name = (el.dataset.templateName || '').toLowerCase()
      const content = (el.dataset.templateContent || '').toLowerCase()
      const ok = !q || name.includes(q) || content.includes(q)
      el.style.display = ok ? '' : 'none'
    })
  }

  // 공개 답글 선택 개수 업데이트 및 저장 버튼 활성/비활성
  updatePublicSelectionCount() {
    try {
      const actionType = this.getCurrentActionType()
      const requiresPublicSelection = (actionType === 'public_reply' || actionType === 'reply_and_dm')

      if (requiresPublicSelection) {
        const checked = this.element.querySelectorAll('input[name="comment_monitoring_rule[settings][public_reply_template_ids][]"]:checked')
        const count = checked ? checked.length : 0
        if (this.hasPublicSelectionCountTarget) {
          this.publicSelectionCountTarget.textContent = count
        }
        const minOk = count >= 3
        if (this.hasSubmitButtonTarget) {
          this.submitButtonTarget.disabled = !minOk
          this.submitButtonTarget.classList.toggle('opacity-50', !minOk)
          this.submitButtonTarget.classList.toggle('cursor-not-allowed', !minOk)
        }
      } else {
        // DM 또는 관리자 알림 등 공개 답댓글 선택 요건이 없는 경우: 저장 버튼 활성화로 복귀
        if (this.hasSubmitButtonTarget) {
          this.submitButtonTarget.disabled = false
          this.submitButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
        }
      }
    } catch (e) {}
  }

  handlePostsFrameLoad() {
    // Turbo Frame이 갱신된 후 기존 선택 상태 복원
    this.highlightSelectedPost()
  }

  highlightSelectedPost() {
    if (!this.hasPostIdTarget) return
    const selectedId = (this.postIdTarget.value || '').trim()
    const postsGrid = document.getElementById('postsGrid')
    if (!postsGrid) return

    // 기존 선택 표시 초기화
    postsGrid.querySelectorAll('.post-selected').forEach((post) => {
      post.classList.remove('post-selected', 'bg-blue-100', 'border-blue-500')
    })
    postsGrid.querySelectorAll('.selected-indicator').forEach((indicator) => {
      indicator.classList.remove('scale-100')
      indicator.classList.add('scale-0')
    })

    if (!selectedId) return

    const esc = (window.CSS && typeof window.CSS.escape === 'function') ? window.CSS.escape(selectedId) : selectedId.replace(/"/g, '\\"')
    const selectedCard = postsGrid.querySelector(`[data-post-id="${esc}"]`)
    if (!selectedCard) return

    selectedCard.classList.add('post-selected', 'bg-blue-100', 'border-blue-500')
    const indicator = selectedCard.querySelector('.selected-indicator')
    if (indicator) {
      indicator.classList.remove('scale-0')
      indicator.classList.add('scale-100')
    }

    const postUrl = selectedCard.dataset.postUrl
    if (this.hasPostUrlTarget && postUrl) {
      this.postUrlTarget.value = postUrl
    }
  }


  // 게시물 선택 처리
  selectPost(event) {
    const postCard = event.currentTarget
    const postId = postCard.dataset.postId
    const postUrl = postCard.dataset.postUrl

    // 이미 선택된 포스트인지 확인
    const isAlreadySelected = postCard.classList.contains("post-selected")

    console.log("📱 포스트 클릭:", { postId, postUrl, isAlreadySelected })

    // 이전에 선택된 게시물의 선택 상태 제거
    const postsGrid = document.querySelector('#postsGrid')
    if (postsGrid) {
      const selectedPosts = postsGrid.querySelectorAll(".post-selected")
      selectedPosts.forEach(post => {
        post.classList.remove("post-selected", "bg-blue-100", "border-blue-500")
        const indicator = post.querySelector('.selected-indicator')
        if (indicator) {
          indicator.classList.remove('scale-100')
          indicator.classList.add('scale-0')
        }
      })
    }

    // 이미 선택된 포스트를 다시 클릭한 경우 선택 해제
    if (isAlreadySelected) {
      console.log("🔄 포스트 선택 해제:", postId)

      // 히든 필드 초기화
      if (this.hasPostIdTarget) {
        this.postIdTarget.value = ""
        console.log("🗑️ 포스트 ID 초기화됨")
      }
      if (this.hasPostUrlTarget) {
        this.postUrlTarget.value = ""
      }

      // "모든 댓글" 모드 비활성화
      this.disableAllCommentsMode()
      return
    }

    // 새로운 포스트 선택
    console.log("✅ 포스트 선택:", postId)
    postCard.classList.add("post-selected", "bg-blue-100", "border-blue-500")

    // 선택 표시 원 활성화
    const indicator = postCard.querySelector('.selected-indicator')
    if (indicator) {
      indicator.classList.remove('scale-0')
      indicator.classList.add('scale-100')
    }

    // 히든 필드에 선택된 게시물 ID 설정
    if (this.hasPostIdTarget) {
      this.postIdTarget.value = postId
      console.log("✅ 포스트 ID 설정됨:", postId)
    }

    // 포스트 URL도 필요한 경우 설정 (현재는 사용하지 않음)
    if (this.hasPostUrlTarget) {
      this.postUrlTarget.value = postUrl
    }

    // 포스트 선택 시 "모든 댓글" 모드 활성화
    this.enableAllCommentsMode()
  }

  // 중복 정의 제거됨

  // 템플릿 선택 처리 (라디오 버튼용)
  handleTemplateChange(event) {
    if (!event.target.checked) return // 라디오 버튼이 선택되지 않은 경우 무시
    
    const templateId = event.target.value
    const selectedLabel = event.target.closest('label')
    
    if (this.hasChatbotNotificationTarget) {
      // 선택된 템플릿이 AI 생성 타입인지 확인 (라벨 텍스트 또는 data attribute로 판단)
      const isAITemplate = selectedLabel && (
        selectedLabel.textContent.includes("AI 챗봇") || 
        selectedLabel.textContent.includes("🤖") ||
        selectedLabel.querySelector('.bg-yellow-100') // AI 생성 템플릿의 배지 색상
      )
      
      if (isAITemplate) {
        this.chatbotNotificationTarget.style.display = "flex"
        const useChatbotCheckbox = document.getElementById("comment_monitoring_rule_use_chatbot")
        if (useChatbotCheckbox) {
          useChatbotCheckbox.checked = true
        }
      } else {
        this.chatbotNotificationTarget.style.display = "none"
        const useChatbotCheckbox = document.getElementById("comment_monitoring_rule_use_chatbot")
        if (useChatbotCheckbox) {
          useChatbotCheckbox.checked = false
        }
      }
    }
    
    console.log("🔄 템플릿 선택됨:", templateId)
  }

  // 빠른 생성 탭 전환
  switchQuickCreate(event) {
    try {
      const btn = event.currentTarget
      const tab = btn?.dataset?.tab
      if (!tab) return
      const root = btn.closest('[data-quick-root]')
      if (!root) return

      // 패널 토글
      const panels = root.querySelectorAll('[data-quick-panel]')
      panels.forEach((p) => {
        if (p.getAttribute('data-quick-panel') === tab) {
          p.classList.remove('hidden')
        } else {
          p.classList.add('hidden')
        }
      })

      // 탭 스타일 업데이트
      const tabs = root.querySelectorAll('[data-tab]')
      tabs.forEach((t) => {
        const active = t.getAttribute('data-tab') === tab
        t.classList.toggle('bg-blue-600', active)
        t.classList.toggle('text-white', active)
        t.classList.toggle('text-gray-700', !active)
        t.classList.toggle('hover:bg-gray-50', !active)
      })

      this.updateQuickPreview()
    } catch (e) {
      console.warn('switchQuickCreate error', e)
    }
  }

  // 빠른 생성 영역 초기화
  initializeQuickCreateForms() {
    try {
      this.quickRootTargets.forEach((root) => this.initializeQuickRoot(root))
    } catch (e) {
      console.warn('initializeQuickCreateForms error', e)
    }
  }

  initializeQuickRoot(root) {
    if (!root) return

    const buttonContainer = this.buttonRowContainer(root)
    if (buttonContainer && buttonContainer.children.length === 0) {
      this.addButtonRowToRoot(root)
    } else if (buttonContainer) {
      this.updateButtonRowStates(root)
    }

    const cardContainer = this.cardButtonRowContainer(root)
    if (cardContainer && cardContainer.children.length === 0) {
      this.addCardButtonRowToRoot(root)
    } else if (cardContainer) {
      this.updateCardButtonRowStates(root)
    }

    this.updateCardImageStageForRoot(root)
    this.updateQuickPreviewForRoot(root)
  }

  findQuickRoot(element) {
    if (!element) return null
    return element.closest('[data-quick-root]')
  }

  buttonRowContainer(root) {
    return root?.querySelector('[data-role="button-row-container"]')
  }

  buttonRowTemplate(root) {
    return root?.querySelector('template[data-role="button-row-template"]')
  }

  cardButtonRowContainer(root) {
    return root?.querySelector('[data-role="card-button-row-container"]')
  }

  cardButtonRowTemplate(root) {
    return root?.querySelector('template[data-role="card-button-row-template"]')
  }

  // 버튼 템플릿 행 추가
  addButtonRow(event) {
    event?.preventDefault()
    const root = this.findQuickRoot(event?.currentTarget)
    this.addButtonRowToRoot(root)
  }

  addButtonRowToRoot(root) {
    const container = this.buttonRowContainer(root)
    const template = this.buttonRowTemplate(root)
    if (!container || !template) return
    if (container.querySelectorAll('[data-button-row]').length >= 3) return

    const fragment = template.content.cloneNode(true)
    container.appendChild(fragment)
    this.updateButtonRowStates(root)
    this.updateQuickPreviewForRoot(root)
  }

  removeButtonRow(event) {
    event.preventDefault()
    const row = event.currentTarget.closest('[data-button-row]')
    const root = this.findQuickRoot(row)
    if (!row || !root) return
    row.remove()
    const container = this.buttonRowContainer(root)
    if (container && container.children.length === 0) {
      this.addButtonRowToRoot(root)
    } else {
      this.updateButtonRowStates(root)
      this.updateQuickPreviewForRoot(root)
    }
  }

  updateButtonRowStates(root) {
    const container = this.buttonRowContainer(root)
    if (!container) return
    const rows = Array.from(container.querySelectorAll('[data-button-row]'))
    const removable = rows.length > 1

    rows.forEach((row, index) => {
      row.dataset.index = index
      const removeBtn = row.querySelector('[data-role="remove-button"]')
      if (removeBtn) {
        removeBtn.classList.toggle('hidden', !removable)
        removeBtn.disabled = !removable
      }

      const typeSelect = row.querySelector('[data-role="button-type"]')
      if (typeSelect) {
        this.updateButtonRowPlaceholder(row, typeSelect.value)
      }

      const indexLabel = row.querySelector('[data-role="button-index"]')
      if (indexLabel) {
        indexLabel.textContent = index + 1
      }
    })

    const addButton = root.querySelector('[data-role="add-button-row"]')
    if (addButton) {
      const disabled = rows.length >= 3
      addButton.disabled = disabled
      addButton.classList.toggle('opacity-50', disabled)
      addButton.classList.toggle('cursor-not-allowed', disabled)
    }

    this.validateQuickButtonRows(root)
  }

  handleButtonTypeChange(event) {
    const row = event.currentTarget.closest('[data-button-row]')
    if (!row) return
    this.updateButtonRowPlaceholder(row, event.currentTarget.value)
  }

  updateButtonRowPlaceholder(row, type) {
    const valueWrapper = row.querySelector('[data-role="button-value-wrapper"]')
    const valueInput = row.querySelector('[data-role="button-value"]')
    const templateWrapper = row.querySelector('[data-role="button-template-wrapper"]')
    const templateSelect = row.querySelector('[data-role="button-template-select"]')
    const hint = row.querySelector('[data-role="button-hint"]')
    if (!hint) return

    const helper = {
      url: {
        placeholder: this.buttonValuePlaceholderUrlValue || 'e.g. https://example.com',
        hint: this.buttonHintUrlValue || 'Opens a link when tapped.'
      },
      phone: {
        placeholder: this.buttonValuePlaceholderPhoneValue || 'e.g. 01012345678',
        hint: this.buttonHintPhoneValue || 'Dials this number when tapped.'
      },
      postback: {
        placeholder: this.buttonValuePlaceholderPostbackValue || 'e.g. POSTBACK_PAYLOAD',
        hint: this.buttonHintPostbackValue || 'Enter a payload key for automation.'
      },
      template_call: {
        placeholder: '',
        hint: this.buttonHintTemplateCallValue || 'Select a template to link.'
      }
    }

    helper.web_url = helper.url

    const config = helper[type] || helper.url

    if (type === 'template_call') {
      if (valueWrapper) valueWrapper.classList.add('hidden')
      if (templateWrapper) templateWrapper.classList.remove('hidden')
      if (valueInput) valueInput.value = ''
    } else {
      if (valueWrapper) valueWrapper.classList.remove('hidden')
      if (templateWrapper) templateWrapper.classList.add('hidden')
      if (valueInput && config.placeholder) {
        valueInput.placeholder = config.placeholder
      }
      if (templateSelect) templateSelect.value = ''
    }

    hint.dataset.defaultHint = config.hint
    hint.textContent = config.hint
  }

  // 카드 버튼 행 추가
  addCardButtonRow(event) {
    event?.preventDefault()
    const root = this.findQuickRoot(event?.currentTarget)
    this.addCardButtonRowToRoot(root)
  }

  removeCardButtonRow(event) {
    event.preventDefault()
    const row = event.currentTarget.closest('[data-card-button-row]')
    const root = this.findQuickRoot(row)
    if (!row || !root) return
    row.remove()
    const container = this.cardButtonRowContainer(root)
    if (container && container.children.length === 0) {
      this.addCardButtonRowToRoot(root)
    } else {
      this.updateCardButtonRowStates(root)
      this.updateQuickPreviewForRoot(root)
    }
  }

  addCardButtonRowToRoot(root) {
    const container = this.cardButtonRowContainer(root)
    const template = this.cardButtonRowTemplate(root)
    if (!container || !template) return
    if (container.querySelectorAll('[data-card-button-row]').length >= 3) return

    const fragment = template.content.cloneNode(true)
    container.appendChild(fragment)
    this.updateCardButtonRowStates(root)
    this.updateQuickPreviewForRoot(root)
  }

  updateCardButtonRowStates(root) {
    const container = this.cardButtonRowContainer(root)
    if (!container) return
    const rows = Array.from(container.querySelectorAll('[data-card-button-row]'))
    const removable = rows.length > 1

    rows.forEach((row, index) => {
      row.dataset.index = index
      const removeBtn = row.querySelector('[data-role="remove-card-button"]')
      if (removeBtn) {
        removeBtn.classList.toggle('hidden', !removable)
        removeBtn.disabled = !removable
      }

      const typeSelect = row.querySelector('[data-role="card-button-type"]')
      if (typeSelect) {
        this.updateCardButtonRowPlaceholder(row, typeSelect.value)
      }

      const indexLabel = row.querySelector('[data-role="card-button-index"]')
      if (indexLabel) {
        indexLabel.textContent = index + 1
      }
    })

    const addButton = root.querySelector('[data-role="add-card-button-row"]')
    if (addButton) {
      const disabled = rows.length >= 3
      addButton.disabled = disabled
      addButton.classList.toggle('opacity-50', disabled)
      addButton.classList.toggle('cursor-not-allowed', disabled)
    }

    this.validateQuickCardButtonRows(root)
  }

  handleCardButtonTypeChange(event) {
    const row = event.currentTarget.closest('[data-card-button-row]')
    if (!row) return
    this.updateCardButtonRowPlaceholder(row, event.currentTarget.value)
  }

  updateCardButtonRowPlaceholder(row, type) {
    const valueWrapper = row.querySelector('[data-role="card-button-value-wrapper"]')
    const valueInput = row.querySelector('[data-role="card-button-value"]')
    const templateWrapper = row.querySelector('[data-role="card-button-template-wrapper"]')
    const templateSelect = row.querySelector('[data-role="card-button-template-select"]')
    const hint = row.querySelector('[data-role="card-button-hint"]')
    if (!hint) return

    const helper = {
      web_url: {
        placeholder: this.cardButtonValuePlaceholderWebUrlValue || 'e.g. https://example.com',
        hint: this.cardButtonHintWebUrlValue || 'Opens a link when tapped.'
      },
      phone: {
        placeholder: this.cardButtonValuePlaceholderPhoneValue || 'e.g. 01012345678',
        hint: this.cardButtonHintPhoneValue || 'Dials this number when tapped.'
      },
      postback: {
        placeholder: this.cardButtonValuePlaceholderPostbackValue || 'e.g. CARD_POSTBACK',
        hint: this.cardButtonHintPostbackValue || 'Enter a payload key for automation.'
      },
      template_call: {
        placeholder: '',
        hint: this.cardButtonHintTemplateCallValue || 'Select a template to link.'
      }
    }

    helper.url = helper.web_url

    const config = helper[type] || helper.web_url

    if (type === 'template_call') {
      if (valueWrapper) valueWrapper.classList.add('hidden')
      if (templateWrapper) templateWrapper.classList.remove('hidden')
      if (valueInput) valueInput.value = ''
    } else {
      if (valueWrapper) valueWrapper.classList.remove('hidden')
      if (templateWrapper) templateWrapper.classList.add('hidden')
      if (valueInput && config.placeholder) {
        valueInput.placeholder = config.placeholder
      }
      if (templateSelect) templateSelect.value = ''
    }

    hint.dataset.defaultHint = config.hint
    hint.textContent = config.hint
  }

  validateQuickButtonRows(root) {
    const container = this.buttonRowContainer(root)
    if (!container) return
    const rows = Array.from(container.querySelectorAll('[data-button-row]'))

    rows.forEach((row) => {
      const title = row.querySelector('[data-role="button-title"]')?.value.trim()
      const type = row.querySelector('[data-role="button-type"]')?.value
      const hint = row.querySelector('[data-role="button-hint"]')
      if (!hint) return

      const inputEl = type === 'template_call'
        ? row.querySelector('[data-role="button-template-select"]')
        : row.querySelector('[data-role="button-value"]')

      if (!inputEl) return
      const hasError = !!title && !this.fieldValuePresent(type, inputEl)
      this.applyFieldValidationState(inputEl, hint, hasError)
    })
  }

  validateQuickCardButtonRows(root) {
    const container = this.cardButtonRowContainer(root)
    if (!container) return
    const rows = Array.from(container.querySelectorAll('[data-card-button-row]'))

    rows.forEach((row) => {
      const title = row.querySelector('[data-role="card-button-title"]')?.value.trim()
      const type = row.querySelector('[data-role="card-button-type"]')?.value
      const hint = row.querySelector('[data-role="card-button-hint"]')
      if (!hint) return

      const inputEl = type === 'template_call'
        ? row.querySelector('[data-role="card-button-template-select"]')
        : row.querySelector('[data-role="card-button-value"]')

      if (!inputEl) return
      const hasError = !!title && !this.fieldValuePresent(type, inputEl)
      this.applyFieldValidationState(inputEl, hint, hasError)
    })
  }

  fieldValuePresent(type, input) {
    if (!input) return false
    if (type === 'template_call') {
      return input.value && input.value.trim().length > 0
    }
    if (input.tagName === 'INPUT' || input.tagName === 'TEXTAREA') {
      return input.value && input.value.trim().length > 0
    }
    return !!input.value
  }

  // Type labels for button previews
  buttonTypeLabel(type) {
    switch (type) {
      case 'url':
        return this.buttonTypeLabelUrlValue || 'Link'
      case 'web_url':
        return this.buttonTypeLabelWebUrlValue || this.buttonTypeLabelUrlValue || 'Link'
      case 'phone':
        return this.buttonTypeLabelPhoneValue || 'Phone'
      case 'postback':
        return this.buttonTypeLabelPostbackValue || 'Postback'
      case 'template_call':
        return this.buttonTypeLabelTemplateCallValue || 'Reply with template'
      default:
        return this.buttonTypeLabelUrlValue || 'Link'
    }
  }

  cardButtonTypeLabel(type) {
    // card uses web_url instead of url
    if (type === 'url') type = 'web_url'
    return this.buttonTypeLabel(type)
  }

  applyFieldValidationState(input, hint, hasError) {
    if (!input || !hint) return
    const defaultHint = hint.dataset.defaultHint || hint.textContent || ''

    if (hasError) {
      input.classList.add('border-red-300', 'ring-1', 'ring-red-200')
      hint.textContent = this.valueRequiredMessageValue || 'Please enter a value.'
      hint.classList.add('text-red-500')
    } else {
      input.classList.remove('border-red-300', 'ring-1', 'ring-red-200')
      hint.textContent = defaultHint
      hint.classList.remove('text-red-500')
    }
  }

  updateQuickPreview() {
    try {
      this.quickRootTargets.forEach((root) => this.updateQuickPreviewForRoot(root))
    } catch (e) {
      console.warn('updateQuickPreview error', e)
    }
  }

  updateQuickPreviewForRoot(root) {
    this.updateTextPreview(root)
    this.updateButtonPreview(root)
    this.updateCardPreview(root)
    this.validateQuickButtonRows(root)
    this.validateQuickCardButtonRows(root)
    this.refreshContentCounters(root)
  }

  updateTextPreview(root) {
    const previewTarget = root?.querySelector('[data-comment-monitoring-target="textPreview"]')
    const form = root?.querySelector('form[data-template-type="text"]')
    if (!previewTarget || !form) return

    const contentInput = form.querySelector('textarea[name="content"]')
    const content = contentInput ? contentInput.value.trim() : ''
    const substituted = this.applySampleVariables(content)

    const messageBody = substituted
      ? `<div class="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">${this.escapeHtml(substituted)}</div>`
      : `<div class=\"text-sm text-gray-400 italic\">${this.previewTextPlaceholderValue || 'Example response will appear here.'}</div>`

    const previewHtml = `
      <div class="flex items-start gap-2">
        <div class="flex-shrink-0">
          <div class="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 text-xs font-semibold text-white">${this.avatarMeLabelValue || 'Me'}</div>
        </div>
        <div class="flex-1">
          <div class="inline-block max-w-[320px] rounded-2xl rounded-tl-sm bg-blue-50 px-3 py-2 text-sm shadow-sm ring-1 ring-blue-100">
            ${messageBody}
          </div>
          <div class="mt-1 text-[11px] text-gray-400">${this.timeJustNowValue || 'just now'}</div>
        </div>
      </div>
    `

    previewTarget.innerHTML = previewHtml
  }

  updateButtonPreview(root) {
    const previewTarget = root?.querySelector('[data-comment-monitoring-target="buttonPreview"]')
    const form = root?.querySelector('form[data-template-type="button_template"]')
    const container = root?.querySelector('[data-role="button-row-container"]')
    if (!previewTarget || !form || !container) return

    const nameInput = form.querySelector('input[name="name"]')
    const bodyInput = form.querySelector('textarea[name="content"]')
    const rows = Array.from(container.querySelectorAll('[data-button-row]'))

    const name = nameInput ? nameInput.value.trim() : ''
    const rawBody = bodyInput ? bodyInput.value.trim() : ''
    const body = this.applySampleVariables(rawBody)

    const buttons = rows
      .map((row) => {
        const title = row.querySelector('[data-role="button-title"]')?.value.trim()
        const type = row.querySelector('[data-role="button-type"]')?.value
        let value
        if (type === 'template_call') {
          value = row.querySelector('[data-role="button-template-select"]')?.value
        } else {
          value = row.querySelector('[data-role="button-value"]')?.value.trim()
        }
        if (!title) return null
        return { title, type, value }
      })
      .filter(Boolean)

    const headingHtml = name
      ? `<div class="text-[13px] font-semibold text-gray-900">${this.escapeHtml(name)}</div>`
      : `<div class=\"text-[13px] font-semibold text-gray-400\">${this.templateNamePlaceholderValue || 'Template name'}</div>`

    const bodyHtml = body
      ? `<div class="mt-1 text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">${this.escapeHtml(body)}</div>`
      : `<div class=\"mt-1 text-sm text-gray-400 italic\">${this.buttonBodyPlaceholderValue || 'Intro text will appear here.'}</div>`

    let buttonsHtml = ''
    if (buttons.length > 0) {
      buttonsHtml = '<div class="mt-3 space-y-1.5">'
      buttons.forEach((btn) => {
        const label = this.escapeHtml(btn.title)
        const typeHint = this.buttonTypeLabel(btn.type)
        buttonsHtml += `
          <div class="w-full rounded-lg bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm ring-1 ring-gray-200">
            <div class="flex items-center justify-between">
              <span>${label}</span>
              <span class="text-[11px] text-gray-400">${typeHint}</span>
            </div>
          </div>`
      })
      buttonsHtml += '</div>'
    } else {
      buttonsHtml = `<div class=\"mt-3 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-center text-[11px] text-gray-400\">${this.buttonPreviewEmptyPlaceholderValue || 'Add buttons to preview here.'}</div>`
    }

    const previewHtml = `
      <div class="flex items-start gap-2">
        <div class="flex-shrink-0">
          <div class="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-xs font-semibold text-white">${this.avatarMeLabelValue || 'Me'}</div>
        </div>
        <div class="flex-1">
          <div class="inline-block max-w-[320px] rounded-2xl rounded-tl-sm bg-gray-100 px-3 py-2 shadow-sm">
            ${headingHtml}
            ${bodyHtml}
            ${buttonsHtml}
          </div>
          <div class="mt-1 text-[11px] text-gray-400">${this.timeJustNowValue || 'just now'}</div>
        </div>
      </div>
    `

    previewTarget.innerHTML = previewHtml
  }

  updateCardPreview(root) {
    const previewTarget = root?.querySelector('[data-comment-monitoring-target="cardPreview"]')
    const form = root?.querySelector('form[data-template-type="generic_template"]')
    const container = root?.querySelector('[data-role="card-button-row-container"]')
    if (!previewTarget || !form || !container) return

    const titleInput = form.querySelector('input[name="card_title"]')
    const subtitleInput = form.querySelector('[name="card_subtitle"]')
    const nameInput = form.querySelector('input[name="name"]')
    const defaultUrlInput = form.querySelector('input[name="default_url"]')
    const imageUrlInput = form.querySelector('input[name="image_url"]')

    const imageUrl = imageUrlInput ? imageUrlInput.value.trim() : ''
    const rawTitle = (titleInput ? titleInput.value : '').trim()
    const rawSubtitle = (subtitleInput ? subtitleInput.value : '').trim()
    const title = this.applySampleVariables(rawTitle)
    const subtitle = this.applySampleVariables(rawSubtitle)
    const fallbackTitle = (nameInput ? nameInput.value : '').trim()
    const substitutedFallback = this.applySampleVariables(fallbackTitle)
    const defaultUrl = (defaultUrlInput ? defaultUrlInput.value : '').trim()

    this.updateCardImageStageForRoot(root, imageUrl)

    const rows = Array.from(container.querySelectorAll('[data-card-button-row]'))
    const buttons = rows
      .map((row) => {
        const title = row.querySelector('[data-role="card-button-title"]')?.value.trim()
        const type = row.querySelector('[data-role="card-button-type"]')?.value
        let value
        if (type === 'template_call') {
          value = row.querySelector('[data-role="card-button-template-select"]')?.value
        } else {
          value = row.querySelector('[data-role="card-button-value"]')?.value.trim()
        }
        if (!title) return null
        return { title, type, value }
      })
      .filter(Boolean)

    const cardTitle = this.escapeHtml(title || substitutedFallback || (this.cardTitlePlaceholderValue || 'Card title'))
    const cardSubtitle = subtitle
      ? `<div class="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed">${this.escapeHtml(subtitle)}</div>`
      : `<div class=\"text-xs text-gray-400\">${this.cardSubtitlePlaceholderValue || 'Subtitle will appear here.'}</div>`

    let cardButtons = ''
    if (buttons.length > 0) {
      cardButtons = '<div class="mt-3 space-y-2">'
      buttons.forEach((btn) => {
        const label = this.escapeHtml(btn.title)
        const typeHint = this.cardButtonTypeLabel(btn.type)
        cardButtons += `
          <div class="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 shadow-sm">
            <span>${label}</span>
            <span class="text-[11px] text-gray-400">${typeHint}</span>
          </div>`
      })
      cardButtons += '</div>'
    } else {
      cardButtons = `<div class=\"mt-3 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-center text-[11px] text-gray-400\">${this.cardPreviewEmptyPlaceholderValue || 'Add buttons to preview here.'}</div>`
    }

    const imageBlock = imageUrl
      ? `<img src=\"${this.escapeHtml(imageUrl)}\" alt=\"${this.cardImageAltValue || 'Card image'}\" class=\"h-40 w-full rounded-t-xl object-cover\">`
      : `<div class=\"flex h-40 w-full items-center justify-center rounded-t-xl bg-gray-100 text-[12px] text-gray-400\">${this.cardImageNotSetValue || 'No image set'}</div>`

    const previewHtml = `
      <div class="flex items-start gap-2">
        <div class="flex-shrink-0">
          <div class="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 text-xs font-semibold text-white">${this.avatarMeLabelValue || 'Me'}</div>
        </div>
        <div class="flex-1">
          <div class="inline-block max-w-[320px] rounded-2xl rounded-tl-sm bg-gray-100 p-2 shadow-sm">
            <div class="overflow-hidden rounded-xl border border-gray-200 bg-white">
              ${imageBlock}
              <div class="space-y-2 p-3">
                <div class="text-sm font-semibold text-gray-900">${cardTitle}</div>
                ${cardSubtitle}
                ${cardButtons}
              </div>
            </div>
          </div>
          <div class="mt-1 text-[11px] text-gray-400">${this.timeJustNowValue || 'just now'}</div>
        </div>
      </div>
    `

    previewTarget.innerHTML = previewHtml

    const defaultLinkTarget = root.querySelector('[data-comment-monitoring-target="cardPreviewDefaultLink"]')
    if (defaultLinkTarget) {
      if (defaultUrl) {
        const tmpl = this.cardPreviewDefaultLinkWithUrlValue || 'Tap card to open {url}.'
        defaultLinkTarget.textContent = tmpl.replace('{url}', defaultUrl)
      } else {
        defaultLinkTarget.textContent = this.cardPreviewNoDefaultLinkValue || 'No default link set for card.'
      }
    }
  }

  updateCardImageStageForRoot(root, imageUrl = '') {
    const preview = root?.querySelector('[data-comment-monitoring-target="cardImagePreview"]')
    const placeholder = root?.querySelector('[data-comment-monitoring-target="cardImagePlaceholder"]')
    const removeBtn = root?.querySelector('[data-comment-monitoring-target="cardImageRemove"]')
    if (!preview || !placeholder) return

    const url = imageUrl ? imageUrl.trim() : ''

    if (url.length > 0) {
      preview.src = url
      preview.classList.remove('hidden')
      placeholder.classList.add('hidden')
      if (removeBtn) removeBtn.classList.remove('hidden')
    } else {
      preview.src = ''
      preview.classList.add('hidden')
      placeholder.classList.remove('hidden')
      if (removeBtn) removeBtn.classList.add('hidden')
    }
  }

  refreshContentCounters(root) {
    try {
      const buttonCounter = root?.querySelector('[data-comment-monitoring-target="buttonContentCounter"]')
      const buttonForm = root?.querySelector('form[data-template-type="button_template"]')
      if (buttonCounter && buttonForm) {
        const bodyInput = buttonForm.querySelector('textarea[name="content"]')
        const length = bodyInput ? bodyInput.value.trim().length : 0
        const fmt = this.buttonContentCounterFormatValue || '{count} chars (recommended ≤ 500)'
        buttonCounter.textContent = fmt.replace('{count}', length)
        buttonCounter.classList.toggle('text-red-500', length > 500)
      }

      const textCounter = root?.querySelector('[data-comment-monitoring-target="textContentCounter"]')
      const textForm = root?.querySelector('form[data-template-type="text"]')
      if (textCounter && textForm) {
        const textInput = textForm.querySelector('textarea[name="content"]')
        const length = textInput ? textInput.value.trim().length : 0
        const fmt = this.textContentCounterFormatValue || '{count} chars (recommended ≤ 500)'
        textCounter.textContent = fmt.replace('{count}', length)
        textCounter.classList.toggle('text-red-500', length > 500)
      }
    } catch (e) {
      console.warn('refreshContentCounters error', e)
    }
  }

  afterQuickCreateReset(form) {
    if (!form) return

    const root = this.findQuickRoot(form)
    if (!root) return

    const templateType = form.dataset.templateType

    if (templateType === 'button_template') {
      const container = this.buttonRowContainer(root)
      if (container) {
        container.innerHTML = ''
        this.addButtonRowToRoot(root)
      }
    }

    if (templateType === 'generic_template') {
      const container = this.cardButtonRowContainer(root)
      if (container) {
        container.innerHTML = ''
        this.addCardButtonRowToRoot(root)
      }
      this.updateCardImageStageForRoot(root, '')
      const defaultLinkTarget = root.querySelector('[data-comment-monitoring-target="cardPreviewDefaultLink"]')
      if (defaultLinkTarget) {
        defaultLinkTarget.textContent = this.cardPreviewNoDefaultLinkValue || 'No default link set for card.'
      }
    }

    this.refreshContentCounters(root)
  }

  // 액션 타입 변경 처리 (새로운 버전)
  handleActionTypeChange(event) {
    const actionType = event ? event.target.value : this.getCurrentActionType()
    console.log("🔄 액션 타입 변경:", actionType)

    // 템플릿 선택기 동적 업데이트
    if (actionType) {
      this.updateTemplateSelector(actionType)
    }

    this.toggleNotificationChannelOptions(actionType)

     // 액션 전환에 맞춰 저장 버튼 상태 재평가 (DM/관리자 알림에서 비활성 고착 방지)
     this.updatePublicSelectionCount()
  }

  // 현재 선택된 액션 타입 가져오기
  getCurrentActionType() {
    const actionTypes = ['public_reply', 'private_reply', 'reply_and_dm', 'admin_notification']
    
    for (const type of actionTypes) {
      const radio = document.getElementById(`comment_monitoring_rule_action_type_${type}`)
      if (radio && radio.checked) {
        return type
      }
    }
    
    return null
  }

  toggleNotificationChannelOptions(actionType) {
    if (!this.hasNotificationChannelsTarget) return

    if (actionType === 'admin_notification') {
      this.notificationChannelsTarget.classList.remove('hidden')
    } else {
      this.notificationChannelsTarget.classList.add('hidden')
    }
  }

  // 템플릿 선택기 업데이트
  async updateTemplateSelector(actionType) {
    if (!this.hasTemplateSelectorContainerTarget) {
      console.warn("⚠️ 템플릿 선택기 컨테이너를 찾을 수 없음")
      return
    }

    try {
      const url = buildLocalizedUrl(`/monitoring/update_template_selector?action_type=${encodeURIComponent(actionType)}`)
      
      // Turbo를 사용하여 컴포넌트 업데이트
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content
        }
      })

      if (response.ok) {
        const turboStream = await response.text()
        Turbo.renderStreamMessage(turboStream)
        // 새 마크업 적용 후 카운트/필터 재적용
        await new Promise((r) => setTimeout(r, 0))
          this.updatePublicSelectionCount()
          this.filterTemplates()
        return true
      } else {
        console.error("템플릿 선택기 업데이트 실패:", response.status)
      }
    } catch (error) {
      console.error("템플릿 선택기 업데이트 오류:", error)
    }
  }

  // 레거시 액션 타입 변경 처리 (호환성을 위해 유지)
  handleActionTypeChangeLegacy() {
    console.log("⚙️ 레거시 액션 타입 변경 처리")
    // 새로운 방식 사용
    this.handleActionTypeChange()
  }

  // 인라인 간편 템플릿 생성
  async quickCreateTemplate(event) {
    event.preventDefault()
    try {
      const form = event.target.closest('form')
      const nameInput = form.querySelector('input[name="name"]')
      const contentInput = form.querySelector('[name="content"]')
      const name = nameInput ? nameInput.value.trim() : ''
      const content = contentInput ? contentInput.value.trim() : ''
      const responseType = form.querySelector('input[name="response_type"]').value
      const templateInput = form.querySelector('input[name="template_type"]')
      const templateType = templateInput ? templateInput.value : null

      if (!name) {
        alert(this.quickCreateNameRequiredMessageValue || 'Please enter template name.')
        nameInput?.focus()
        return
      }

      const mt = { name, response_type: responseType }
      const ttype = templateType || form.dataset.templateType || 'text'
      mt.template_type = ttype
      if (ttype === 'text') {
        if (!content) {
          alert(this.quickCreateContentRequiredMessageValue || 'Please enter response content.')
          contentInput?.focus()
          return
        }
        mt.content = content
      } else if (ttype === 'button_template') {
        // 수집: 최대 3개의 버튼 행
        const rows = form.querySelectorAll('[data-button-row]')
        const buttons = []
        let invalidValue = null
        rows.forEach((row, index) => {
          const title = row.querySelector('input[name="btn_title"]').value.trim()
          const type = row.querySelector('select[name="btn_type"]').value
          const val = row.querySelector('input[name="btn_value"]').value.trim()
          if (!title) return
          if (!val) {
            invalidValue = { index: index + 1, type }
            return
          }
          const btn = { title: title, type: type }
          if (type === 'url') btn.url = val
          else if (type === 'phone') btn.phone_number = val
          else if (type === 'postback') btn.payload = val
          else if (type === 'template_call') btn.template_id = val
          buttons.push(btn)
        })
        if (invalidValue) {
          this.validateQuickButtonRows()
          const msgTmpl = this.quickCreateMissingButtonValueAtMessageValue || '{index}th button missing required value.'
          alert(msgTmpl.replace('{index}', invalidValue.index))
          return
        }
        if (buttons.length === 0) {
          alert(this.quickCreateMinButtonsRequiredMessageValue || 'Enter at least one button.')
          return
        }
        if (content) mt.content = content
        mt.button_templates = buttons
      } else if (ttype === 'generic_template') {
        // 카드 1개 기준
        const el = {}
        const title = form.querySelector('input[name="card_title"]').value.trim()
        const subtitle = form.querySelector('[name="card_subtitle"]').value.trim()
        const imageUrl = form.querySelector('input[name="image_url"]').value.trim()
        const defaultUrl = form.querySelector('input[name="default_url"]').value.trim()
        if (title) {
          el.title = title
        }
        if (subtitle) el.subtitle = subtitle
        if (imageUrl) el.image_url = imageUrl
        if (defaultUrl) el.default_action = { type: 'web_url', url: defaultUrl }

        if (!el.title) {
          el.title = name
        }

        const rows = form.querySelectorAll('[data-card-button-row]')
        const buttons = []
        let invalidValue = null
        rows.forEach((row, index) => {
          const btitle = row.querySelector('input[name="btn_title"]').value.trim()
          const btype = row.querySelector('select[name="btn_type"]').value
          const bval = row.querySelector('input[name="btn_value"]').value.trim()
          if (!btitle) return
          if (!bval) {
            invalidValue = { index: index + 1, type: btype }
            return
          }
          const btn = { title: btitle, type: btype }
          if (btype === 'web_url') btn.url = bval
          else if (btype === 'phone') btn.phone_number = bval
          else if (btype === 'postback') btn.payload = bval
          else if (btype === 'template_call') btn.template_id = bval
          buttons.push(btn)
        })
        if (invalidValue) {
          this.validateQuickCardButtonRows()
          const msgTmpl = this.quickCreateMissingButtonPromptAtMessageValue || 'Please enter required value for button #{index}.'
          alert(msgTmpl.replace('{index}', invalidValue.index))
          return
        }
        if (buttons.length > 0) el.buttons = buttons
        mt.generic_elements = [el]
      }

      const body = { message_template: mt }
      const csrf = document.querySelector('[name="csrf-token"]').content
      const res = await fetch(buildLocalizedUrl('/message_templates'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrf
        },
        body: JSON.stringify(body)
      })
      if (!res.ok) {
        console.error('템플릿 생성 실패', res.status)
        alert(this.quickCreateCreateFailedMessageValue || 'Failed to create template. Check inputs.')
        return
      }
      const data = await res.json()
      const createdId = data.id
      const currentAction = this.getCurrentActionType() || (responseType === 'public_reply' ? 'public_reply' : 'private_reply')

      // 선택기 새로고침 후 자동 선택
      await this.updateTemplateSelector(currentAction)
      if (responseType === 'public_reply' || currentAction === 'reply_and_dm') {
        const cb = this.element.querySelector(`input[name="comment_monitoring_rule[settings][public_reply_template_ids][]"][value="${createdId}"]`)
        if (cb) { cb.checked = true }
        this.updatePublicSelectionCount()
      } else {
        const rb = this.element.querySelector(`input[name="comment_monitoring_rule[message_template_id]"][value="${createdId}"]`)
        if (rb) { rb.checked = true }
      }

      // 폼 초기화
      form.reset()
      this.afterQuickCreateReset(form)
      this.updateQuickPreview()
    } catch (e) {
      console.error('quickCreateTemplate error', e)
      alert(this.quickCreateUnexpectedErrorMessageValue || 'Unexpected error occurred.')
    }
  }
  
  // 초기 모드 상태 설정
  initializeModeState() {
    // 포스트 선택 여부 확인
    const postIdInput = this.element.querySelector('input[name="comment_monitoring_rule[post_id]"]')
    const hasPostSelected = !!(postIdInput && postIdInput.value && postIdInput.value.trim())

    if (hasPostSelected) {
      // 포스트가 선택된 경우 "모든 댓글" 모드 활성화
      this.enableAllCommentsMode()
    } else {
      // 포스트가 선택되지 않은 경우 "모든 댓글" 모드 비활성화
      this.disableAllCommentsMode()
    }

    console.log("🎯 초기 모드 상태 설정 완료, 포스트 선택됨:", hasPostSelected)
  }

  // 검색/정렬 관련 핸들러는 제거

  // "모든 댓글" 모드 활성화
  enableAllCommentsMode() {
    const allModeRadio = document.getElementById('response_mode_all')
    const allModeLabel = document.querySelector('.response-mode-all')
    // 가장 가까운 상위 컨테이너에서 경고 문구를 찾도록 수정
    const warningText = allModeLabel?.closest('.mb-4')?.querySelector('.text-amber-600')

    if (allModeRadio) {
      // disabled 속성/프로퍼티 모두 해제
      allModeRadio.disabled = false
      if (allModeRadio.hasAttribute('disabled')) {
        allModeRadio.removeAttribute('disabled')
      }
      // 스타일 업데이트
      const span = allModeLabel?.querySelector('span')
      const description = allModeLabel?.querySelector('div')
      if (span) span.classList.remove('text-gray-400')
      if (description) {
        description.classList.remove('text-gray-400')
        description.classList.add('text-gray-500')
      }
    }

    if (warningText) {
      warningText.style.display = 'none'
    }

    console.log("✅ 모든 댓글 모드 활성화됨, 경고 문구 숨김:", !!warningText)

    // 적용 범위 배지 업데이트 (선택한 포스트)
    try {
    if (this.hasScopeBadgeTarget) {
      this.scopeBadgeTarget.textContent = (this.scopeSelectedPostLabelValue || 'Selected post scope')
    }
      // 키워드 범위 안내 숨김
      if (this.hasKeywordsScopeNoteTarget) {
        this.keywordsScopeNoteTarget.classList.add('hidden')
      }
    } catch (e) { console.warn('scope badge update (enable) error:', e) }
  }

  // "모든 댓글" 모드 비활성화
  disableAllCommentsMode() {
    const allModeRadio = document.getElementById('response_mode_all')
    const allModeLabel = document.querySelector('.response-mode-all')
    // 가장 가까운 상위 컨테이너에서 경고 문구를 찾도록 수정
    const warningText = allModeLabel?.closest('.mb-4')?.querySelector('.text-amber-600')

    if (allModeRadio) {
      allModeRadio.disabled = true
      // disabled 속성도 명시적으로 추가
      allModeRadio.setAttribute('disabled', 'disabled')
      // 스타일 업데이트
      const span = allModeLabel?.querySelector('span')
      const description = allModeLabel?.querySelector('div')
      if (span) span.classList.add('text-gray-400')
      if (description) {
        description.classList.remove('text-gray-500')
        description.classList.add('text-gray-400')
      }
    }

    if (warningText) {
      warningText.style.display = 'block'
    }

    console.log("❌ 모든 댓글 모드 비활성화됨, 경고 문구 표시:", !!warningText)

    // 적용 범위 배지 업데이트 (계정 전체)
    try {
    if (this.hasScopeBadgeTarget) {
      this.scopeBadgeTarget.textContent = (this.scopeAccountWideLabelValue || 'Account-wide scope')
    }
      // 현재 모드가 키워드면 안내 노출
      if (this.hasKeywordsScopeNoteTarget) {
        const mode = this.getSelectedMode()
        if (mode === 'keyword') {
          this.keywordsScopeNoteTarget.classList.remove('hidden')
        } else {
          this.keywordsScopeNoteTarget.classList.add('hidden')
        }
      }
    } catch (e) { console.warn('scope badge update (disable) error:', e) }
  }

  // "키워드 기준" 모드 선택
  selectKeywordMode() {
    const keywordModeRadio = document.getElementById('response_mode_keyword')
    if (keywordModeRadio && !keywordModeRadio.checked) {
      keywordModeRadio.checked = true
      // 모드 변경 이벤트 트리거
      this.syncModeUI()
      console.log("🔄 키워드 기준 모드로 전환됨")
    }
  }
}
